import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./AddDoctor.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import { 
    FaTimes, 
    FaUserMd, 
    FaUser, 
    FaEnvelope, 
    FaPhone, 
    FaCalendarAlt, 
    FaVenusMars,
    FaStethoscope,
    FaGraduationCap,
    FaIdCard,
    FaMoneyBillWave,
    FaLanguage,
    FaFileAlt,
    FaSave,
    FaArrowLeft,
    FaUndo,
    FaUpload,
    FaTrash,
    FaPlus,
    FaTimesCircle
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonField = () => (
    <div className={classes.skeletonField}>
        <div className={classes.skeletonLabel}></div>
        <div className={classes.skeletonInput}></div>
    </div>
);

const SkeletonGrid3 = () => (
    <div className={classes.grid3}>
        {Array(3).fill(0).map((_, i) => (
            <SkeletonField key={i} />
        ))}
    </div>
);

const SkeletonSection = ({ title }) => (
    <div className={classes.skeletonSection}>
        <div className={classes.skeletonTitle}></div>
        <div className={classes.skeletonContent}>
            <SkeletonGrid3 />
        </div>
    </div>
);

const AddDoctor = () => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    
    // Profile photo state
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState("");

    // Languages state
    const [languagesList, setLanguagesList] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState("");

    // Form state - Updated to match API
    const [form, setForm] = useState({
        // Personal Information
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone_country_code: "+234",
        phone_number: "",
        date_of_birth: "",
        gender: "",
        marital_status: "",
        nationality: "",
        department_id: "",
        // Professional Information
        specialization: "",
        sub_specialization: "",
        qualification: "",
        license_number: "",
        registration_number: "",
        experience_years: "",
        consultation_fee: "",
        status: "",
        // Additional Information
        address: "",
        languages_spoken: [], // Array for multiple languages
        about: "",
        notes: "",
        // Account Information
        create_account: true,
        username: "",
        password: "",
        password_confirmation: "",
    });

    // Department options - You'll need to fetch these from API or define them
    const departments = [
        { id: 1, name: "Cardiology" },
        { id: 2, name: "Neurology" },
        { id: 3, name: "Orthopedics" },
        { id: 4, name: "Pediatrics" },
        { id: 5, name: "Gynecology" },
        { id: 6, name: "Dermatology" },
        { id: 7, name: "Ophthalmology" },
        { id: 8, name: "ENT" },
        { id: 9, name: "Urology" },
        { id: 10, name: "Nephrology" },
        { id: 11, name: "Endocrinology" },
        { id: 12, name: "Oncology" },
        { id: 13, name: "Radiology" },
        { id: 14, name: "Pathology" },
        { id: 15, name: "Anesthesiology" },
        { id: 16, name: "Psychiatry" },
        { id: 17, name: "Dentistry" },
        { id: 18, name: "Physiotherapy" },
        { id: 19, name: "Emergency Medicine" },
        { id: 20, name: "Family Medicine" },
    ];

    // Specialization options
    const specializations = [
        "Cardiologist",
        "Neurosurgeon",
        "Orthopedic Surgeon",
        "Pediatrician",
        "Gynecologist",
        "Dermatologist",
        "Ophthalmologist",
        "ENT Specialist",
        "Urologist",
        "Nephrologist",
        "Endocrinologist",
        "Oncologist",
        "Radiologist",
        "Pathologist",
        "Anesthesiologist",
        "Psychiatrist",
        "Dentist",
        "Physiotherapist",
        "Emergency Physician",
        "Family Physician",
    ];

    // Status options
    const statusOptions = ["active", "inactive", "on_leave", "suspended"];

    // Language options
    const languageOptions = [
        "English",
        "Yoruba",
        "Hausa",
        "Igbo",
        "French",
        "Spanish",
        "Arabic",
        "Chinese",
        "German",
        "Italian",
        "Portuguese",
        "Russian",
        "Japanese",
        "Korean",
        "Dutch",
        "Swahili",
        "Zulu",
        "Afrikaans",
        "Hindi",
        "Urdu",
    ];

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setSubmitError("");
        setSubmitSuccess("");
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Profile photo must be less than 2MB',
                    confirmButtonColor: '#2F80ED',
                });
                return;
            }
            if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please upload PNG or JPG image',
                    confirmButtonColor: '#2F80ED',
                });
                return;
            }
            setProfilePhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        setProfilePhotoPreview("");
    };

    // Handle adding a language
    const handleAddLanguage = () => {
        if (!selectedLanguage) {
            Swal.fire({
                icon: 'warning',
                title: 'Please select a language',
                confirmButtonColor: '#2F80ED',
            });
            return;
        }
        if (form.languages_spoken.includes(selectedLanguage)) {
            Swal.fire({
                icon: 'warning',
                title: 'Language already added',
                text: `${selectedLanguage} is already in the list`,
                confirmButtonColor: '#2F80ED',
            });
            return;
        }
        setForm((prev) => ({
            ...prev,
            languages_spoken: [...prev.languages_spoken, selectedLanguage]
        }));
        setSelectedLanguage("");
    };

    // Handle removing a language
    const handleRemoveLanguage = (languageToRemove) => {
        setForm((prev) => ({
            ...prev,
            languages_spoken: prev.languages_spoken.filter(lang => lang !== languageToRemove)
        }));
    };

    const handleReset = () => {
        setForm({
            first_name: "",
            middle_name: "",
            last_name: "",
            email: "",
            phone_country_code: "+234",
            phone_number: "",
            date_of_birth: "",
            gender: "",
            marital_status: "",
            nationality: "",
            department_id: "",
            specialization: "",
            sub_specialization: "",
            qualification: "",
            license_number: "",
            registration_number: "",
            experience_years: "",
            consultation_fee: "",
            status: "",
            address: "",
            languages_spoken: [],
            about: "",
            notes: "",
            create_account: true,
            username: "",
            password: "",
            password_confirmation: "",
        });
        setSelectedLanguage("");
        setProfilePhoto(null);
        setProfilePhotoPreview("");
        setSubmitError("");
        setSubmitSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitSuccess("");
        setIsSubmitting(true);

        // Validate required fields
        const errors = [];
        if (!form.first_name) errors.push("First Name is required");
        if (!form.last_name) errors.push("Last Name is required");
        if (!form.email) errors.push("Email Address is required");
        if (!form.phone_number) errors.push("Phone Number is required");
        if (!form.date_of_birth) errors.push("Date of Birth is required");
        if (!form.gender) errors.push("Gender is required");
        if (!form.department_id) errors.push("Department is required");
        if (!form.specialization) errors.push("Specialization is required");
        if (!form.qualification) errors.push("Qualification is required");
        if (!form.license_number) errors.push("License Number is required");
        if (!form.experience_years) errors.push("Experience is required");
        if (!form.consultation_fee) errors.push("Consultation Fee is required");
        if (!form.status) errors.push("Status is required");
        if (form.create_account) {
            if (!form.username) errors.push("Username is required");
            if (!form.password) errors.push("Password is required");
            if (form.password.length < 8) errors.push("Password must be at least 8 characters");
            if (form.password !== form.password_confirmation) errors.push("Passwords do not match");
        }

        if (errors.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Please fix the following errors',
                html: `<div style="text-align: left; font-size: 14px;">${errors.map((e, i) => `${i+1}. ${e}`).join('\n')}</div>`,
                confirmButtonColor: '#2F80ED',
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const companyId = localStorage.getItem("companyId") || "1";
            
            // Prepare payload - Match API exactly
            const payload = {
                company_id: parseInt(companyId),
                first_name: form.first_name,
                middle_name: form.middle_name || null,
                last_name: form.last_name,
                email: form.email,
                phone_country_code: form.phone_country_code || null,
                phone_number: form.phone_number,
                date_of_birth: form.date_of_birth,
                gender: form.gender,
                marital_status: form.marital_status || null,
                nationality: form.nationality || null,
                department_id: parseInt(form.department_id),
                specialization: form.specialization,
                sub_specialization: form.sub_specialization || null,
                qualification: form.qualification,
                license_number: form.license_number,
                registration_number: form.registration_number || null,
                experience_years: parseFloat(form.experience_years),
                consultation_fee: parseFloat(form.consultation_fee),
                status: form.status,
                address: form.address || null,
                languages_spoken: form.languages_spoken.length > 0 ? form.languages_spoken : null,
                about: form.about || null,
                notes: form.notes || null,
                create_account: form.create_account,
                username: form.create_account ? form.username : null,
                password: form.create_account ? form.password : null,
                password_confirmation: form.create_account ? form.password_confirmation : null,
            };

            console.log("Creating Doctor with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/api/doctor/create`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            console.log("Doctor Created:", response.data);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Doctor added successfully!',
                confirmButtonColor: '#2F80ED',
                timer: 2000,
                timerProgressBar: true,
            });

            setSubmitSuccess("Doctor added successfully!");
            
            setTimeout(() => {
                navigate("/doctors");
            }, 2000);

        } catch (error) {
            console.error("Error creating doctor:", error);
            
            let errorMsg = "Failed to add doctor. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === 'object') {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey && Array.isArray(message[firstKey])) {
                            errorMsg = message[firstKey][0];
                        } else {
                            errorMsg = JSON.stringify(message);
                        }
                    } else if (typeof message === 'string') {
                        errorMsg = message;
                    }
                } else if (error.response.data?.errors) {
                    const errors = error.response.data.errors;
                    const firstKey = Object.keys(errors)[0];
                    if (firstKey && errors[firstKey][0]) {
                        errorMsg = errors[firstKey][0];
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            
            setSubmitError(errorMsg);
            
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMsg,
                confirmButtonColor: '#2F80ED',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render skeleton loader
    if (isLoading) {
        return (
            <div className={classes.pageContainer}>
                <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
                <div className={classes.mainArea}>
                    <Header toggleSidebar={toggleSidebar} />
                    <div className={classes.content}>
                        <div className={classes.skeletonContainer}>
                            <div className={classes.skeletonHeader}>
                                <div className={classes.skeletonTitle} style={{ width: '200px' }}></div>
                                <div className={classes.skeletonButton}></div>
                            </div>
                            <div className={classes.twoColSkeleton}>
                                <div className={classes.skeletonLeft}>
                                    <SkeletonSection title="Personal Information" />
                                </div>
                                <div className={classes.skeletonRight}>
                                    <SkeletonSection title="Profile Photo" />
                                </div>
                            </div>
                            <div className={classes.twoColSkeleton}>
                                <div className={classes.skeletonLeft}>
                                    <SkeletonSection title="Professional Information" />
                                </div>
                                <div className={classes.skeletonRight}>
                                    <SkeletonSection title="Account Information" />
                                </div>
                            </div>
                            <div className={classes.twoColSkeleton}>
                                <div className={classes.skeletonLeft}>
                                    <SkeletonSection title="Additional Information" />
                                </div>
                                <div className={classes.skeletonRight}>
                                    <SkeletonSection title="Notes" />
                                </div>
                            </div>
                            <div className={classes.skeletonFooter}>
                                <div className={classes.skeletonButton}></div>
                                <div className={classes.skeletonButton}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.pageContainer}>
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={classes.mainArea}>
                <Header toggleSidebar={toggleSidebar} />
                <div className={classes.content}>
                    {/* Top Header */}
                    <div className={classes.topHeader}>
                        <div className={classes.headerLeft}>
                            <div className={classes.headerIcon}>
                                <FaUserMd />
                            </div>
                            <div>
                                <h2>Add New Doctor</h2>
                                <p>Fill in the details below to register a new doctor</p>
                            </div>
                        </div>
                        <button className={classes.closeBtn} onClick={() => navigate("/doctors")}>
                            <FaTimes />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className={classes.form}>
                        {/* Success/Error Messages */}
                        {submitSuccess && (
                            <div className={classes.successMessage}>
                                <span>{submitSuccess}</span>
                            </div>
                        )}
                        {submitError && (
                            <div className={classes.submitError}>
                                <span>{submitError}</span>
                            </div>
                        )}

                        {/* ROW 1: Personal Information | Profile Photo */}
                        <div className={classes.twoColLayout}>
                            <div className={classes.leftCol}>
                                {/* PERSONAL INFORMATION */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Personal Information</h3>
                                    
                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>First Name <span className={classes.required}>*</span></label>
                                            <input
                                                type="text"
                                                name="first_name"
                                                placeholder="Enter first name"
                                                className={classes.formInput}
                                                value={form.first_name}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Middle Name</label>
                                            <input
                                                type="text"
                                                name="middle_name"
                                                placeholder="Enter middle name"
                                                className={classes.formInput}
                                                value={form.middle_name}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Last Name <span className={classes.required}>*</span></label>
                                            <input
                                                type="text"
                                                name="last_name"
                                                placeholder="Enter last name"
                                                className={classes.formInput}
                                                value={form.last_name}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className={classes.grid2}>
                                        <div className={classes.inputGroup}>
                                            <label>Email Address <span className={classes.required}>*</span></label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Enter email address"
                                                className={classes.formInput}
                                                value={form.email}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Phone Number <span className={classes.required}>*</span></label>
                                            <div className={classes.phoneInput}>
                                                <span className={classes.phoneCode}>+234</span>
                                                <input
                                                    type="text"
                                                    name="phone_number"
                                                    placeholder="Enter phone number"
                                                    className={classes.formInput}
                                                    value={form.phone_number}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>Date of Birth <span className={classes.required}>*</span></label>
                                            <input
                                                type="date"
                                                name="date_of_birth"
                                                className={classes.formInput}
                                                value={form.date_of_birth}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Gender <span className={classes.required}>*</span></label>
                                            <select
                                                name="gender"
                                                className={classes.formSelect}
                                                value={form.gender}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Marital Status</label>
                                            <select
                                                name="marital_status"
                                                className={classes.formSelect}
                                                value={form.marital_status}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select marital status</option>
                                                <option value="Single">Single</option>
                                                <option value="Married">Married</option>
                                                <option value="Divorced">Divorced</option>
                                                <option value="Widowed">Widowed</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className={classes.inputGroup}>
                                        <label>Nationality</label>
                                        <input
                                            type="text"
                                            name="nationality"
                                            placeholder="Enter nationality"
                                            className={classes.formInput}
                                            value={form.nationality}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={classes.rightCol}>
                                {/* PROFILE PHOTO */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Profile Photo</h3>
                                    
                                    <div className={classes.photoUpload}>
                                        {profilePhotoPreview ? (
                                            <div className={classes.photoPreview}>
                                                <img src={profilePhotoPreview} alt="Profile" />
                                                <button 
                                                    type="button" 
                                                    className={classes.removePhotoBtn}
                                                    onClick={removePhoto}
                                                    disabled={isSubmitting}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className={classes.uploadArea}>
                                                <FaUpload className={classes.uploadIcon} />
                                                <p>Drag and drop or click to browse</p>
                                                <span>PNG, JPG up to 2MB</span>
                                                <input
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/jpg"
                                                    onChange={handlePhotoChange}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ROW 2: Professional Information | Account Information */}
                        <div className={classes.twoColLayout}>
                            <div className={classes.leftCol}>
                                {/* PROFESSIONAL INFORMATION */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Professional Information</h3>
                                    
                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>Department <span className={classes.required}>*</span></label>
                                            <select
                                                name="department_id"
                                                className={classes.formSelect}
                                                value={form.department_id}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select department</option>
                                                {departments.map((dept) => (
                                                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Specialization <span className={classes.required}>*</span></label>
                                            <select
                                                name="specialization"
                                                className={classes.formSelect}
                                                value={form.specialization}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select specialization</option>
                                                {specializations.map((spec) => (
                                                    <option key={spec} value={spec}>{spec}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Sub Specialization</label>
                                            <input
                                                type="text"
                                                name="sub_specialization"
                                                placeholder="Enter sub specialization"
                                                className={classes.formInput}
                                                value={form.sub_specialization}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>Qualification <span className={classes.required}>*</span></label>
                                            <input
                                                type="text"
                                                name="qualification"
                                                placeholder="Enter qualification (e.g., MBBS, MD)"
                                                className={classes.formInput}
                                                value={form.qualification}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>License Number <span className={classes.required}>*</span></label>
                                            <input
                                                type="text"
                                                name="license_number"
                                                placeholder="Enter medical license number"
                                                className={classes.formInput}
                                                value={form.license_number}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Registration Number</label>
                                            <input
                                                type="text"
                                                name="registration_number"
                                                placeholder="Enter registration number"
                                                className={classes.formInput}
                                                value={form.registration_number}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>Experience (Years) <span className={classes.required}>*</span></label>
                                            <input
                                                type="number"
                                                name="experience_years"
                                                placeholder="Enter years of experience"
                                                className={classes.formInput}
                                                value={form.experience_years}
                                                onChange={handleChange}
                                                min="0"
                                                step="0.5"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Consultation Fee <span className={classes.required}>*</span></label>
                                            <input
                                                type="number"
                                                name="consultation_fee"
                                                placeholder="Enter consultation fee"
                                                className={classes.formInput}
                                                value={form.consultation_fee}
                                                onChange={handleChange}
                                                min="0"
                                                step="0.01"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Status <span className={classes.required}>*</span></label>
                                            <select
                                                name="status"
                                                className={classes.formSelect}
                                                value={form.status}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select status</option>
                                                {statusOptions.map((opt) => (
                                                    <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.rightCol}>
                                {/* ACCOUNT INFORMATION */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Account Information</h3>
                                    
                                    <div className={classes.checkboxGroup}>
                                        <label className={classes.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                name="create_account"
                                                checked={form.create_account}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                            Create new user account
                                        </label>
                                    </div>

                                    {form.create_account && (
                                        <>
                                            <div className={classes.inputGroup}>
                                                <label>Username <span className={classes.required}>*</span></label>
                                                <input
                                                    type="text"
                                                    name="username"
                                                    placeholder="Enter username"
                                                    className={classes.formInput}
                                                    value={form.username}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className={classes.inputGroup}>
                                                <label>Password <span className={classes.required}>*</span></label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    placeholder="Enter password (min 8 characters)"
                                                    className={classes.formInput}
                                                    value={form.password}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div className={classes.inputGroup}>
                                                <label>Confirm Password <span className={classes.required}>*</span></label>
                                                <input
                                                    type="password"
                                                    name="password_confirmation"
                                                    placeholder="Confirm password"
                                                    className={classes.formInput}
                                                    value={form.password_confirmation}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ROW 3: Additional Information (3 cols) | Notes */}
                        <div className={classes.twoColLayout}>
                            <div className={classes.leftCol}>
                                {/* ADDITIONAL INFORMATION */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Additional Information</h3>
                                    
                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>Address</label>
                                            <textarea
                                                name="address"
                                                placeholder="Enter full address"
                                                className={classes.formTextarea}
                                                value={form.address}
                                                onChange={handleChange}
                                                rows="3"
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        {/* Languages Spoken - Multi-select with chips */}
                                        <div className={classes.inputGroup}>
                                            <label>Language Spoken</label>
                                            <div className={classes.languageInputWrapper}>
                                                <select
                                                    name="languages_spoken_select"
                                                    className={classes.formSelect}
                                                    value={selectedLanguage}
                                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="">Select languages</option>
                                                    {languageOptions.map((lang) => (
                                                        <option key={lang} value={lang}>{lang}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className={classes.addLanguageBtn}
                                                    onClick={handleAddLanguage}
                                                    disabled={isSubmitting}
                                                >
                                                    <FaPlus />
                                                </button>
                                            </div>
                                            {form.languages_spoken.length > 0 && (
                                                <div className={classes.languageChips}>
                                                    {form.languages_spoken.map((lang) => (
                                                        <span key={lang} className={classes.languageChip}>
                                                            {lang}
                                                            <button
                                                                type="button"
                                                                className={classes.removeChipBtn}
                                                                onClick={() => handleRemoveLanguage(lang)}
                                                                disabled={isSubmitting}
                                                            >
                                                                <FaTimesCircle />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className={classes.inputGroup}>
                                            <label>About Doctor</label>
                                            <textarea
                                                name="about"
                                                placeholder="Enter brief description about the doctor"
                                                className={classes.formTextarea}
                                                value={form.about}
                                                onChange={handleChange}
                                                rows="3"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.rightCol}>
                                {/* NOTES */}
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Notes</h3>
                                    
                                    <div className={classes.inputGroup}>
                                        <textarea
                                            name="notes"
                                            placeholder="Enter any additional notes..."
                                            className={classes.formTextarea}
                                            value={form.notes}
                                            onChange={handleChange}
                                            rows="6"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FOOTER */}
                        <div className={classes.footer}>
                            <div className={classes.note}>
                                All fields marked with <span className={classes.required}>*</span> are required.
                            </div>
                            <div className={classes.footerButtons}>
                                <button 
                                    type="button" 
                                    className={classes.backBtn} 
                                    onClick={() => navigate("/doctors")}
                                    disabled={isSubmitting}
                                >
                                    <FaArrowLeft /> Back to Doctors
                                </button>
                                <button 
                                    type="button" 
                                    className={classes.resetBtn} 
                                    onClick={handleReset}
                                    disabled={isSubmitting}
                                >
                                    <FaUndo /> Reset
                                </button>
                                <button 
                                    type="submit" 
                                    className={classes.saveBtn}
                                    disabled={isSubmitting}
                                >
                                    <FaSave /> {isSubmitting ? "Saving..." : "Save Doctor"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddDoctor;